import { Entitlements } from '@experiences/constants';
import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { useRouteResolver } from '@experiences/util';
import { useMemo } from 'react';

import * as RouteNames from '../../../../common/constants/RouteNames';
import useCanAccessUsageInsights from '../../../../common/hooks/useCanAccessUsageInsights';
import { useEntitlements } from '../../../entitlements/EntitlementsContextProvider';
import {
    UNIT_TYPE_QUERY_PARAM,
    VALID_UNIT_TYPES,
} from '../../consumptionDashboard/utils';

// "View details" link to the consumption dashboard: only for supported unit types, and gated by
// EnableConsumptionDashboardView (plus EnableConsumptionDashboardSap for SAP organizations, and
// EnableUsageInsightsForCommunity for Community organizations).
export const useConsumptionDashboardLink = (consumableCode: string, tenantId?: string) => {
    const getRoute = useRouteResolver();
    const enableConsumptionDashboardView = useFeatureFlagValue(Features.EnableConsumptionDashboardView.name);
    const enableConsumptionDashboardSap = useFeatureFlagValue(Features.EnableConsumptionDashboardSap.name);
    const { hasEntitlement } = useEntitlements();
    const isSapOrganization = hasEntitlement(Entitlements.PlatformSapEnabled);
    const canAccessUsageInsights = useCanAccessUsageInsights();
    const enableConsumptionDashboard = enableConsumptionDashboardView
        && canAccessUsageInsights
        && (!isSapOrganization || enableConsumptionDashboardSap);

    const hasConsumptionDashboard = useMemo(() =>
        VALID_UNIT_TYPES.includes(consumableCode as typeof VALID_UNIT_TYPES[number])
    , [ consumableCode ]);

    const consumptionDashboardHref = useMemo(() => {
        if (!hasConsumptionDashboard) {
            return undefined;
        }
        const route = tenantId
            ? getRoute(RouteNames.LicensingTenantConsumptionDashboard.replace(':tenantId', tenantId))
            : getRoute(RouteNames.LicensingConsumptionDashboard);
        const params = new URLSearchParams();
        params.set(UNIT_TYPE_QUERY_PARAM, consumableCode);
        return `${route}?${params.toString()}`;
    }, [ consumableCode, getRoute, hasConsumptionDashboard, tenantId ]);

    return {
        enableConsumptionDashboard,
        hasConsumptionDashboard,
        consumptionDashboardHref,
    };
};
